import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAtom } from "jotai";

import { getApiBaseUrl } from "../config/api";
import { useToken } from "../context/AuthContext";
import { reporterAiModeAtom, type ReporterAiMode } from "../atoms/reporterPrefsAtoms";

const QUERY_KEY = ["userPrefs"] as const;

interface UserPrefsResponse {
  prefs: {
    reporter_ai_mode: ReporterAiMode;
  };
}

async function fetchUserPrefs(token: string): Promise<UserPrefsResponse> {
  const { data } = await axios.get<UserPrefsResponse>(`${getApiBaseUrl()}/api/user_prefs`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

async function postUserPrefs(token: string, mode: ReporterAiMode): Promise<UserPrefsResponse> {
  const { data } = await axios.post<UserPrefsResponse>(
    `${getApiBaseUrl()}/api/user_prefs`,
    { reporter_ai_mode: mode },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
}

/**
 * Read + write the caller's reporter A/B preferences. The atom
 * `reporterAiModeAtom` is the single source of truth — this hook writes
 * to it on query success and on mutate, and reads from it for `mode`.
 * Consumers that don't need the mutation can subscribe to the atom directly
 * and see identical values.
 *
 * Optimistic update on `setMode` flips the atom immediately; the network
 * round-trip happens in the background. If the server rejects the write the
 * atom is rolled back to the cached value.
 *
 * `isLoading` distinguishes "atom holds its default while the query is in
 * flight" from "atom holds the user's persisted mode". Callers that
 * auto-react to mode changes (e.g. reset-on-flip) MUST gate on
 * `!isLoading`, otherwise the loading-default-to-hydrated transition is
 * indistinguishable from a real user flip.
 */
export function useUserPrefs() {
  const token = useToken();
  const queryClient = useQueryClient();
  const [mode, setModeAtom] = useAtom(reporterAiModeAtom);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const data = await fetchUserPrefs(token!);
      setModeAtom(data.prefs.reporter_ai_mode);
      return data;
    },
    enabled: !!token,
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: (next: ReporterAiMode) => postUserPrefs(token!, next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<UserPrefsResponse>(QUERY_KEY);
      queryClient.setQueryData<UserPrefsResponse>(QUERY_KEY, {
        prefs: { reporter_ai_mode: next },
      });
      setModeAtom(next);
      return { previous };
    },
    onError: (_err, _next, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
        setModeAtom(context.previous.prefs.reporter_ai_mode);
      }
    },
  });

  return {
    mode,
    isLoading: query.isLoading,
    setMode: mutation.mutate,
  };
}
